import React, { useContext, useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import {
    CssBaseline,
    Box,
    Typography,
    Button,
    TextField,
    Paper,
    Container,
    Avatar,
    InputAdornment,
    Stack,
    alpha,
    GlobalStyles
} from '@mui/material';
import LockPersonRoundedIcon from '@mui/icons-material/LockPersonRounded'; // Icona più MD3
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import { InfoContext as SPInfoContext } from '../context/InfoContext';
import { clientsApi } from '../apiUser';
import { useNavigate } from 'react-router-dom';
import WaitingDialog from '../components/WaitingDialog';

// --- CONFIGURAZIONE TEMA MD3 (On-the-fly per questa pagina) ---
// Colori Aziendali: #24476A (Primary), #336699 (Secondary/Tertiary)
const md3Theme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            main: '#24476A',
            light: '#537297',
            dark: '#002040',
            contrastText: '#FFFFFF',
        },
        secondary: {
            main: '#336699',
            light: '#6593ca',
            dark: '#003d6a',
            contrastText: '#FFFFFF',
        },
        background: {
            default: '#FDFBFF',
            paper: '#FDFBFF',
        },
        text: {
            primary: '#1A1C1E',
            secondary: '#43474E',
        },
    },
    shape: {
        borderRadius: 16, // MD3 standard corner radius
    },
    typography: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        h4: { fontWeight: 600, letterSpacing: '-0.5px' },
        button: { textTransform: 'none', fontWeight: 500, letterSpacing: '0.1px' },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 24, // Pill shape buttons
                    padding: '12px 24px',
                    fontSize: '1rem',
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: 12, // Input fields slightly more squared than buttons
                },
            },
        },
    },
});

// --- STILI MD3 GLASS ---
const glassSurfaceStyle = {
    // Sfondo semi-trasparente ma leggibile (Surface Container High)
    bgcolor: alpha('#FFFFFF', 0.75), 
    backdropFilter: 'blur(20px) saturate(180%)',
    boxShadow: '0 8px 32px 0 rgba(36, 71, 106, 0.15)', // Ombra colorata col tuo blu
    border: '1px solid rgba(255, 255, 255, 0.6)', 
    borderRadius: '28px', // MD3 Large shape
};

const LoginPage = (props) => {
    const [id, setId] = useState('');
    const [pin, setPin] = useState('');
    const [idError, setIdError] = useState('');
    const [pinError, setPinError] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const { info, setInfo } = useContext(SPInfoContext);
    const navigate = useNavigate();

    const handleIdChange = (e) => {
        setIdError('');
        const value = e.target.value.replace(/[^0-9]/g, '');
        if (value.length <= 3) setId(value);
    };

    const handlePinChange = (e) => {
        setPinError('');
        const value = e.target.value.replace(/[^0-9]/g, '');
        if (value.length <= 5) setPin(value);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIdError('');
        setPinError('');

        let valid = true;
        if (id.length !== 3 || !/^\d{3}$/.test(id)) {
            setIdError('Inserisci un ID di 3 cifre');
            valid = false;
        }
        if (pin.length !== 5 || !/^\d{5}$/.test(pin)) {
            setPinError('Inserisci un PIN di 5 cifre');
            valid = false;
        }

        if (!valid) return;

        setOpenDialog(true);

        try {
            if (id === "000" && pin === "00001") {
                const response = await clientsApi.get();
                setInfo((prevInfo) => ({ ...prevInfo, utenti: response.data }));
                props.onAdmin(true);
                navigate('/sp/devices');
                return;
            }

            const response = await clientsApi.login({ client_id: id, password: pin });

            if (!response.data || !response.data.jwt) {
                alert('Login fallito: Risposta server non valida.');
                setOpenDialog(false);
                return;
            }

            localStorage.setItem('accessToken', response.data.jwt);
            setInfo((prevInfo) => ({ ...prevInfo, selectedClient: response.data }));

            setTimeout(() => {
                navigate('/dsmm/home');
            }, 800);
        } catch (error) {
            console.error('Error:', error);
            setPinError('Credenziali non valide.');
        } finally {
            setOpenDialog(false);
        }
    };

    return (
        <Container component="main" maxWidth="xs" sx={{ display: 'flex', alignItems: 'center', height: '100vh', justifyContent: 'center' }}>
            <WaitingDialog open={openDialog} onClose={() => setOpenDialog(false)} message="Accesso in corso..." />
            
            <Paper elevation={0} sx={{ ...glassSurfaceStyle, p: { xs: 4, sm: 5 }, width: '100%', maxWidth: 420 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    
                    {/* Icona Header MD3 */}
                    <Box sx={{ 
                        mb: 3, p: 2, 
                        bgcolor: alpha('#24476A', 0.1), // Primary container color
                        color: '#24476A', // On Primary Container
                        borderRadius: '16px' 
                    }}>
                        <LockPersonRoundedIcon sx={{ fontSize: 40 }} />
                    </Box>

                    <Typography component="h1" variant="h4" color="onSurface" sx={{ mb: 1, fontWeight: 700 }}>
                        Sistemi POS
                    </Typography>
                    
                    <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 4 }}>
                        Accedi alla tua dashboard DSMM24
                    </Typography>

                    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
                        <Stack spacing={3}>
                            <TextField
                                required fullWidth id="id" label="ID Cliente (3 Cifre)" name="id"
                                autoComplete="off" type="text"
                                value={id} onChange={handleIdChange}
                                error={!!idError} helperText={idError}
                                placeholder="000"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <PersonRoundedIcon color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                                // Stile MD3 personalizzato per l'input
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: alpha('#FFF', 0.5),
                                        '& fieldset': { borderColor: alpha('#24476A', 0.2) },
                                        '&:hover fieldset': { borderColor: '#24476A' },
                                        '&.Mui-focused fieldset': { borderWidth: 2, borderColor: '#24476A' },
                                    }
                                }}
                            />
                            
                            <TextField
                                required fullWidth name="pin" label="PIN (5 Cifre)" type="password" id="pin"
                                value={pin} onChange={handlePinChange}
                                error={!!pinError} helperText={pinError}
                                placeholder="•••••"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <KeyRoundedIcon color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: alpha('#FFF', 0.5),
                                        '& fieldset': { borderColor: alpha('#24476A', 0.2) },
                                        '&:hover fieldset': { borderColor: '#24476A' },
                                        '&.Mui-focused fieldset': { borderWidth: 2, borderColor: '#24476A' },
                                    }
                                }}
                            />
                        </Stack>

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disableElevation
                            sx={{ 
                                mt: 4, mb: 2,
                                bgcolor: '#24476A',
                                '&:hover': { bgcolor: '#336699', boxShadow: '0 4px 12px rgba(36, 71, 106, 0.4)' },
                                height: 56 // Taller button for better touch target
                            }}
                        >
                            Accedi
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </Container>
    );
};

// --- APP WRAPPER ---
export function Login(props) {
    return (
        <ThemeProvider theme={md3Theme}>
            <CssBaseline />
            {/* Override globale per scrollbar o font se necessario */}
            <GlobalStyles styles={{ body: { overflow: 'hidden' } }} /> 
            
            <Box
                sx={{
                    minHeight: '100vh',
                    width: '100vw',
                    backgroundImage: `url(/background.jpeg)`, // Assicurati che l'immagine esista in /public
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    
                    // Overlay scuro per migliorare contrasto sfondo/vetro
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: alpha('#001529', 0.4), // Tinta blu scuro invece di nero puro per armonia
                        backdropFilter: 'blur(4px)', // Sfocatura dello sfondo
                        zIndex: 0
                    }
                }}
            >
                {/* Contenuto sopra l'overlay */}
                <Box sx={{ position: 'relative', zIndex: 1, width: '100%' }}>
                    <LoginPage onAdmin={props.onAdmin} />
                </Box>
            </Box>
        </ThemeProvider>
    );
}